import * as React from "react"
import { cva, type VariantProps } from "cva"
import { Slot } from "@radix-ui/react-slot"
import { clx } from "../utils/clx"
import { SpinnerIcon } from "~/design-system"

const buttonVariants = cva({
  base: clx(
    "transition-fg relative inline-flex w-fit items-center justify-center overflow-hidden rounded-md outline-none border",
    "disabled:bg-ui-bg-disabled disabled:text-ui-fg-muted disabled:shadow-none disabled:border-transparent",
  ),
  variants: {
    variant: {
      primary: clx(
        "shadow-buttons-primary bg-blue-base text-grey-0 border-blue-base",
        "hover:bg-blue-dark hover:border-blue-dark",
        "active:bg-blue-darker",
        "focus-visible:shadow-buttons-focus",
      ),
      secondary: clx(
        "shadow-borders-with-shadow border-ui-border-strong text-ui-fg-base bg-grey-0",
        "hover:bg-ui-bg-base-hover",
        "active:bg-grey-10 active:shadow-buttons-neutral-pressed",
        "focus-visible:shadow-borders-interactive-with-active",
      ),
      transparent: clx(
        "text-ui-fg-base bg-transparent border-none",
        "hover:bg-ui-bg-disabled",
        "active:bg-grey-10 active:shadow-buttons-neutral-pressed",
        "focus-visible:shadow-borders-interactive-with-active",
      ),
      link: clx(
        "text-blue-base bg-transparent border-none",
        "hover:underline hover:bg-transparent ",
        "active:shadow-none",
        "focus-visible:underline",
      ),
      danger: clx(
        "shadow-buttons-primary bg-red-base text-grey-0 border-red-base",
        "hover:bg-red-dark hover:border-red-dark",
        "active:bg-red-darker",
        "focus-visible:shadow-buttons-focus",
      ),
    },
    size: {
      small: "txt-compact-small-plus h-8 gap-x-1 px-2 py-1",
      base: "txt-base-regular-plus h-9 gap-x-1.5 px-3.5 py-1.5",
    },
  },
  defaultVariants: {
    size: "base",
    variant: "primary",
  },
})

interface ButtonProps
  extends React.ComponentPropsWithoutRef<"button">,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean
  asChild?: boolean
}

/**
 * This component is based on the `button` element and supports all of its props
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      /**
       * The button's style.
       */
      variant = "primary",
      /**
       * The button's size.
       */
      size = "base",
      className,
      /**
       * Whether to remove the wrapper `button` element and use the
       * passed child element instead.
       */
      asChild = false,
      children,
      /**
       * Whether to show a loading spinner.
       */
      isLoading = false,
      disabled,
      ...props
    }: ButtonProps,
    ref
  ) => {
    const Component = asChild ? Slot : "button"

    /**
     * In the case of a button where asChild is true, and isLoading is true, we ensure that
     * only one element is passed as a child to the Slot component. This is because the Slot
     * component only accepts a single child.
     */
    const renderInner = () => {
      if (isLoading) {
        return (
          <span className="pointer-events-none">
            <div
              className={clx(
                "bg-ui-bg-disabled absolute inset-0 flex items-center justify-center rounded-md"
              )}
            >
              <SpinnerIcon className="animate-spin" />
            </div>
            {children}
          </span>
        )
      }

      return children
    }

    return (
      <Component
        ref={ref}
        {...props}
        className={clx(
          buttonVariants({ variant, size }),
          { "p-0 h-auto": variant === "link" },
          className
          )}
        disabled={disabled || isLoading}
      >
        {renderInner()}
      </Component>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
